import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { productsApi } from '@/api/products'

interface ProductDetailDialogProps {
	productId: number | null
	onClose: () => void
}

export function ProductDetailDialog({ productId, onClose }: ProductDetailDialogProps) {
	const { data: detail, isLoading } = useQuery({
		queryKey: ['product-detail', productId],
		queryFn: () => productsApi.getDetail(productId!),
		enabled: productId !== null,
	})

	return (
		<Dialog open={productId !== null} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
				<DialogHeader><DialogTitle>商品详情</DialogTitle></DialogHeader>
				{isLoading && <div className="py-8 text-center text-muted-foreground">加载中...</div>}
				{detail && (
					<div className="space-y-4">
						<div className="flex gap-3">
							<img src={detail.image} alt={detail.store_name} className="h-24 w-24 rounded-lg object-cover border" />
							<div className="flex-1">
								<h3 className="font-semibold text-lg">{detail.store_name}</h3>
								{detail.store_info && <p className="text-sm text-muted-foreground mt-1">{detail.store_info}</p>}
								<div className="flex gap-4 mt-2">
									<span className="text-lg font-bold text-primary">¥{detail.price}</span>
									{detail.ot_price && <span className="text-sm text-muted-foreground line-through">¥{detail.ot_price}</span>}
									<span className={`text-xs px-2 py-0.5 rounded ${detail.is_show ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
										{detail.is_show ? '上架' : '下架'}
									</span>
								</div>
							</div>
						</div>
						<div className="grid grid-cols-3 gap-3 p-3 bg-muted/30 rounded-lg text-sm">
							<div><span className="text-muted-foreground">库存：</span>{detail.stock}</div>
							<div><span className="text-muted-foreground">销量：</span>{detail.sales}</div>
							<div><span className="text-muted-foreground">单位：</span>{detail.unit_name || '-'}</div>
							<div><span className="text-muted-foreground">规格类型：</span>{detail.spec_type === 1 ? '多规格' : '单规格'}</div>
							<div><span className="text-muted-foreground">排序：</span>{detail.sort ?? 0}</div>
							<div><span className="text-muted-foreground">条码：</span>{detail.bar_code || '-'}</div>
						</div>
						{detail.attr_value && detail.attr_value.length > 0 && (
							<div>
								<h4 className="font-medium mb-2">规格列表</h4>
								<div className="border rounded-lg overflow-hidden">
									<table className="w-full text-sm">
										<thead className="bg-muted/50">
											<tr>
												<th className="text-left p-2">规格</th>
												<th className="text-right p-2">价格</th>
												<th className="text-right p-2">成本</th>
												<th className="text-right p-2">库存</th>
												<th className="text-left p-2">条码</th>
											</tr>
										</thead>
										<tbody>
											{detail.attr_value.map((sku) => (
												<tr key={sku.id} className="border-t">
													<td className="p-2">{sku.suk || '默认'}</td>
													<td className="text-right p-2">¥{sku.price}</td>
													<td className="text-right p-2">¥{sku.cost}</td>
													<td className="text-right p-2">{sku.stock}</td>
													<td className="p-2">{sku.bar_code || '-'}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
